import { useQueryClient } from '@tanstack/react-query';

import type { Season } from '@shared/types';

import { progressFromSeasons, seasonsKey, useContinue, useNextEpisode } from '@/features/show/api';
import { localized } from '@/i18n/localized';
import { useLocale } from '@/i18n/locale';

import { airingBeyondWatched, type UpNextItem } from './model';

/**
 * Everything one Up next title shows and does, shared by every surface that
 * renders one (Tonight's hero and cover rail, the Library rail's
 * `UpNextCard`): the next episode (`useNextEpisode`), watched/total progress
 * (read from the same season-summary cache the optimistic mark patches, so
 * the bar moves with it), the refined NEW flag, and `markNext` (`useContinue`
 * — optimistic, toast + Retry on failure).
 */
export function useUpNextEntry(item: UpNextItem) {
  const { locale, t } = useLocale();
  const qc = useQueryClient();
  const id = item.title.id;
  const name = localized(item.title.title, item.title.titleBr, locale);

  const nextQuery = useNextEpisode(id);
  const next = nextQuery.data ?? null;
  const seasons = qc.getQueryData<Season[]>(seasonsKey(id));
  const progress =
    seasons && seasons.length > 0
      ? progressFromSeasons(seasons)
      : { watched: item.watchedCount, total: item.totalEpisodes };
  const percent = progress.total > 0 ? Math.round((progress.watched / progress.total) * 100) : 0;
  const isNew = Boolean(
    seasons && item.recentAirings.length > 0 && airingBeyondWatched(item.recentAirings, seasons, next),
  );

  const { markNext, isPending } = useContinue(id, {
    status: 'watching',
    watchedCount: progress.watched,
    totalEpisodes: progress.total,
  });

  return {
    id,
    name,
    next,
    nextPending: nextQuery.isPending,
    progress,
    percent,
    isNew,
    markNext,
    isPending,
    /** "S2 · E6" */
    code: next ? t('episode.code', { season: next.season, episode: next.episode }) : '',
    /** "S2·E6" */
    compactCode: next ? t('upnext.codeCompact', { season: next.season, episode: next.episode }) : '',
  };
}
